// src/store/slices/orderKarigarSlice.js
// ============================================================================
// ORDER-KARIGAR INTEGRATION SLICE
// ============================================================================
// Handles state for order-karigar operations:
// - Available karigars for assignment
// - Order karigar status
// - Karigar queue
// - Assignment operations
// ============================================================================

import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAvailableKarigarsForOrderItem,
  fetchBulkKarigarAvailability,
  assignKarigarToOrderItem,
  reassignKarigarForOrderItem,
  bulkAssignKarigars,
  getOrderKarigarStatus,
  getKarigarQueue,
  checkOrderCanFulfill,
  assignKarigarViaOrder,
} from "../../thunks/karigar/orderKarigarThunks";

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
  // Available karigars for assignment
  availableKarigars: {
    productKarigars: [],
    otherKarigars: [],
    allKarigars: [],
    recommendations: {},
    summary: null,
    query: null,
    unavailable: {
      productKarigars: [],
      otherKarigars: [],
    },
  },

  // Order karigar status
  orderStatus: {
    orderId: null,
    hasKarigarWork: false,
    jobs: [],
    summary: null,
    canFulfillOrder: false,
  },

  // Karigar queue
  karigarQueue: {
    karigarId: null,
    karigar: null,
    capacity: null,
    queue: {},
    totalJobs: 0,
    overdueJobs: 0,
  },

  // Can fulfill check result
  canFulfillCheck: null,

  // Last assignment result
  lastAssignment: null,

  // Last reassignment result
  lastReassignment: null,

  // Bulk assignment results
  bulkAssignmentResults: null,

  // Loading states
  loading: {
    availableKarigars: false,
    bulkAvailability: false,
    orderStatus: false,
    karigarQueue: false,
    assign: false,
    reassign: false,
    bulkAssign: false,
    canFulfill: false,
  },

  // Errors
  error: null,

  // Success message
  successMessage: null,
};

// ============================================================================
// SLICE
// ============================================================================

const orderKarigarSlice = createSlice({
  name: "orderKarigar",
  initialState,
  reducers: {
    // Clear available karigars
    clearAvailableKarigars: (state) => {
      state.availableKarigars = initialState.availableKarigars;
    },

    // Clear order status
    clearOrderStatus: (state) => {
      state.orderStatus = initialState.orderStatus;
    },

    // Clear karigar queue
    clearKarigarQueue: (state) => {
      state.karigarQueue = initialState.karigarQueue;
    },

    // Clear can fulfill check
    clearCanFulfillCheck: (state) => {
      state.canFulfillCheck = null;
    },

    // Clear last assignment
    clearLastAssignment: (state) => {
      state.lastAssignment = null;
    },

    // Clear last reassignment
    clearLastReassignment: (state) => {
      state.lastReassignment = null;
    },

    // Clear bulk assignment results
    clearBulkAssignmentResults: (state) => {
      state.bulkAssignmentResults = null;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Clear success message
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },

    // Clear all
    clearAll: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // ========================================================================
    // FETCH AVAILABLE KARIGARS FOR ORDER ITEM
    // ========================================================================
    builder
      .addCase(fetchAvailableKarigarsForOrderItem.pending, (state) => {
        state.loading.availableKarigars = true;
        state.error = null;
      })
      .addCase(
        fetchAvailableKarigarsForOrderItem.fulfilled,
        (state, action) => {
          state.loading.availableKarigars = false;
          state.availableKarigars = {
            productKarigars: action.payload.productKarigars || [],
            otherKarigars: action.payload.otherKarigars || [],
            allKarigars: action.payload.allKarigars || [],
            recommendations: action.payload.recommendations || {},
            summary: action.payload.summary || null,
            query: action.payload.query || null,
            unavailable: action.payload.unavailable || {
              productKarigars: [],
              otherKarigars: [],
            },
          };
        }
      )
      .addCase(fetchAvailableKarigarsForOrderItem.rejected, (state, action) => {
        state.loading.availableKarigars = false;
        state.error = action.payload || "Failed to fetch available karigars";
      });

    // ========================================================================
    // FETCH BULK KARIGAR AVAILABILITY
    // ========================================================================
    builder
      .addCase(fetchBulkKarigarAvailability.pending, (state) => {
        state.loading.bulkAvailability = true;
        state.error = null;
      })
      .addCase(fetchBulkKarigarAvailability.fulfilled, (state, action) => {
        state.loading.bulkAvailability = false;
        state.availableKarigars = {
          productKarigars: action.payload.productKarigars || [],
          otherKarigars: action.payload.otherKarigars || [],
          allKarigars: action.payload.allKarigars || [],
          recommendations: action.payload.recommendations || {},
          summary: action.payload.summary || null,
          query: action.payload.query || null,
          unavailable: action.payload.unavailable || {
            productKarigars: [],
            otherKarigars: [],
          },
        };
      })
      .addCase(fetchBulkKarigarAvailability.rejected, (state, action) => {
        state.loading.bulkAvailability = false;
        state.error = action.payload || "Failed to fetch bulk availability";
      });

    // ========================================================================
    // ASSIGN KARIGAR TO ORDER ITEM
    // ========================================================================
    builder
      .addCase(assignKarigarToOrderItem.pending, (state) => {
        state.loading.assign = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(assignKarigarToOrderItem.fulfilled, (state, action) => {
        state.loading.assign = false;
        state.lastAssignment = action.payload;
        state.successMessage = "Karigar assigned successfully";
      })
      .addCase(assignKarigarToOrderItem.rejected, (state, action) => {
        state.loading.assign = false;
        state.error = action.payload || "Failed to assign karigar";
      });

    // ========================================================================
    // ASSIGN KARIGAR VIA ORDER (Convenience wrapper)
    // ========================================================================
    builder
      .addCase(assignKarigarViaOrder.pending, (state) => {
        state.loading.assign = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(assignKarigarViaOrder.fulfilled, (state, action) => {
        state.loading.assign = false;
        state.lastAssignment = action.payload;
        state.successMessage = "Karigar assigned successfully";
      })
      .addCase(assignKarigarViaOrder.rejected, (state, action) => {
        state.loading.assign = false;
        state.error = action.payload || "Failed to assign karigar";
      });

    // ========================================================================
    // REASSIGN KARIGAR FOR ORDER ITEM
    // ========================================================================
    builder
      .addCase(reassignKarigarForOrderItem.pending, (state) => {
        state.loading.reassign = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(reassignKarigarForOrderItem.fulfilled, (state, action) => {
        state.loading.reassign = false;
        state.lastReassignment = action.payload;
        state.successMessage = "Karigar reassigned successfully";
      })
      .addCase(reassignKarigarForOrderItem.rejected, (state, action) => {
        state.loading.reassign = false;
        state.error = action.payload || "Failed to reassign karigar";
      });

    // ========================================================================
    // BULK ASSIGN KARIGARS
    // ========================================================================
    builder
      .addCase(bulkAssignKarigars.pending, (state) => {
        state.loading.bulkAssign = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(bulkAssignKarigars.fulfilled, (state, action) => {
        state.loading.bulkAssign = false;
        state.bulkAssignmentResults = action.payload;
        const successCount = action.payload?.success?.length || 0;
        const failedCount = action.payload?.failed?.length || 0;
        state.successMessage = `${successCount} assigned, ${failedCount} failed`;
      })
      .addCase(bulkAssignKarigars.rejected, (state, action) => {
        state.loading.bulkAssign = false;
        state.error = action.payload || "Failed to bulk assign karigars";
      });

    // ========================================================================
    // GET ORDER KARIGAR STATUS
    // ========================================================================
    builder
      .addCase(getOrderKarigarStatus.pending, (state) => {
        state.loading.orderStatus = true;
        state.error = null;
      })
      .addCase(getOrderKarigarStatus.fulfilled, (state, action) => {
        state.loading.orderStatus = false;
        state.orderStatus = {
          orderId: action.payload.orderId,
          hasKarigarWork: action.payload.hasKarigarWork,
          jobs: action.payload.jobs || [],
          summary: action.payload.summary || null,
          canFulfillOrder: action.payload.canFulfillOrder || false,
        };
      })
      .addCase(getOrderKarigarStatus.rejected, (state, action) => {
        state.loading.orderStatus = false;
        state.error = action.payload || "Failed to get order karigar status";
      });

    // ========================================================================
    // GET KARIGAR QUEUE
    // ========================================================================
    builder
      .addCase(getKarigarQueue.pending, (state) => {
        state.loading.karigarQueue = true;
        state.error = null;
      })
      .addCase(getKarigarQueue.fulfilled, (state, action) => {
        state.loading.karigarQueue = false;
        state.karigarQueue = {
          karigarId: action.meta.arg.karigarId,
          karigar: action.payload.karigar || null,
          capacity: action.payload.capacity || null,
          queue: action.payload.queue || {},
          totalJobs: action.payload.totalJobs || 0,
          overdueJobs: action.payload.overdueJobs || 0,
        };
      })
      .addCase(getKarigarQueue.rejected, (state, action) => {
        state.loading.karigarQueue = false;
        state.error = action.payload || "Failed to get karigar queue";
      });

    // ========================================================================
    // CHECK ORDER CAN FULFILL
    // ========================================================================
    builder
      .addCase(checkOrderCanFulfill.pending, (state) => {
        state.loading.canFulfill = true;
        state.error = null;
      })
      .addCase(checkOrderCanFulfill.fulfilled, (state, action) => {
        state.loading.canFulfill = false;
        state.canFulfillCheck = action.payload;
      })
      .addCase(checkOrderCanFulfill.rejected, (state, action) => {
        state.loading.canFulfill = false;
        state.error = action.payload || "Failed to check if order can fulfill";
      });
  },
});

// ============================================================================
// EXPORTS
// ============================================================================

export const {
  clearAvailableKarigars,
  clearOrderStatus,
  clearKarigarQueue,
  clearCanFulfillCheck,
  clearLastAssignment,
  clearLastReassignment,
  clearBulkAssignmentResults,
  clearError,
  clearSuccessMessage,
  clearAll,
} = orderKarigarSlice.actions;

export default orderKarigarSlice.reducer;
